import { Popover } from 'antd';
import ShareArticleIcon from 'Components/common/assets/svgIcons/ShareArticleIcon';
import ArticleDrawer from 'Components/common/components/ArticleDrawer';
import SocialShareIcons from 'Components/share/SocialShareIcons';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import setImageSize, { valueToObject } from 'Utils/UtilityFunctions';
import {
  boxShadow, primaryColor, primaryFont, wMedium, maxWidth, retina,
  favouriteArticleColor, whiteColor,
} from 'Utils/utilsStyle';

import { actions as favouriteArticleActions } from 'Slice/FavoriteArticlesSlice';
import FavoriteIcon from 'Components/common/assets/svgIcons/FavoriteIcon';
import { useEffect, useState } from 'react';
import { selectOpenDrawer } from 'Selector/CertificationSelector';
import { actions as flurryActions } from 'Slice/HelperSlice';

type Props = {
  summary: string,
  image: string,
  heading: string,
  domain: string,
  date: string,
  industry: string,
  redirectUrl: string,
  trigger: string,
  articleId: number,
  showDrawer?: boolean;
  isRead?: boolean;
  alertId?: number;
  isFavorite?: boolean;
  articleAlert?: string;

} & typeof defaultProps;

const defaultProps = {
  showDrawer: false,
  isRead: false,
  alertId: 0,
  isFavorite: false,
  articleAlert: '',
};

const Image = styled.div<{ backgroundImage: string }>`
  background-image: url(${(props) => JSON.stringify(props.backgroundImage)});
  height: 80px;
  width: 80px;
  background-position: center;
  background-size: cover;
  border-radius: 10px;
`;

const ArticleWrapper = styled.div<{ tiggerColor: string }>`
  position: relative;
  .ant-popover-inner {
    border-radius: 16px;
    .ant-popover-inner-content{
      padding: 0;
    }
  }
  .text-area {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    padding: 0px 0px 0px 0px;
    width: calc(100% - 60px);
    min-height: 64px;
  }
  .profile-area {
    margin: 0px;
    min-width: 18%;
    @media all and (max-width: ${maxWidth}) , ${retina} {
      min-width: 20%;
    }
    margin-left: 10px;
       &:before {
        content: '';
        height: 100%;
        position: absolute;
        width: 7px !important;
        background-color: ${(props) => props.tiggerColor};
        z-index: 1;
        left: 1px;
        border-radius: 10px 0px 0px 10px;
        top: 0px;
        @media all and (max-width: ${maxWidth}) , ${retina} {
          width: 6px !important;
        }
      }
  }
  .bottom-action-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-left: 12px;
    .bottom-tips {
      max-width: 70%;
    }
    .action-sharing {
      display: flex;
      margin-top: 10px;
      max-width: 30%;
      @media all and (max-width: ${maxWidth}) , ${retina} {
        margin-top: 2px; 
      }
      .popover-sharing {
        height: 10px;
        width: 13px;
        position: relative;
        top: 2px;
        @media all and (max-width:${maxWidth}),${retina}{
          top: 0px;
        }
        .ant-popover-placement-left {
          .ant-popover-content {
            min-width: 238px;
             @media all and (max-width: ${maxWidth}) , ${retina} {
              min-width: 200px;
             }
          }
        }
        .teams-share-button {
          width: 17px;
          height: 13px;
          margin-top: -7px !important;
          top:-7px;
          &:before {
            width: 14px;
            height: 14px;
            top: 7.5px;
            @media all and (max-width: ${maxWidth}) , ${retina} {
              width: 13px;
              height: 13px;
              top: 10px;
            }
          }
        }
        svg {
          @media all and (max-width: ${maxWidth}) , ${retina} {
            width: 18px;
            height: 13px; 
          }
        }
      }
      .favorite-icon {
        height: 20px;
        svg {
          width: 17px;
          height: 20px;
          margin-right: 8px;
           @media all and (max-width: ${maxWidth}) , ${retina} {
            width: 15px;
            height: 18px;
            margin-right: 4px;
           }
          &:hover {
           cursor: pointer;
          }
        }
      }
    }
  }
`;

const ContentWrapper = styled.div`
  min-width: 238px;
  padding: 10px 15px;
  border-radius: 16px;
  box-shadow: ${boxShadow};
  @media all and (max-width: ${maxWidth}) , ${retina} {
    min-width: 200px;
    border-radius: 10px;
    padding: 14px 18px;
  }
  .heading-popover {
    font-family: ${primaryFont};
    font-size: 16px;
    font-weight: ${wMedium};
    color: ${primaryColor};
    @media all and (max-width: ${maxWidth}) , ${retina} {
      font-size: 12px;
    }
  }
`;

const ArticleTag = styled.span <{ tagColor: string }>`
 font-size: 12px;
 padding: 0px 10px;
 color: #fff;
 font-weight: ${wMedium};
 font-family: ${primaryFont};
 border-radius: 12px;
 background-color: ${(props) => props.tagColor};
 line-height: 18px;
 letter-spacing: -0.29px;
 position: absolute;
 right: 5px;
 top: 5px;
 z-index: 1;
  @media all and (max-width: ${maxWidth}), ${retina} {
    font-size: 10px;
    top: -2px;
    right: 0px;
  }
`;

const ArticleItem = function ArticleItem(props: Props) {
  const [visible, setVisible] = useState<boolean>(false);
  const certificationDrawer = useSelector(selectOpenDrawer);

  useEffect(() => {
    if (certificationDrawer && visible) {
      setVisible(false);
    }
  }, [certificationDrawer]);

  const toggleVisible = () => setVisible(!visible);
  const host = window.location.href;
  const dispatch = useDispatch();

  const {
    summary,
    image,
    heading,
    domain,
    date,
    industry,
    redirectUrl,
    trigger,
    articleId,
    showDrawer,
    isRead,
    alertId,
    isFavorite,
    articleAlert,
  } = props;

  const alerts = [{
    alert: articleAlert,
    alertId,
    color: trigger,
  }];

  const content = (
    <ContentWrapper>
      <h6 className="heading-popover">QuickShare to:</h6>
      <SocialShareIcons
        articleid={articleId}
        title={heading}
        img={image}
        domain={domain}
        entity="article"
        alertId={alertId}
        url={redirectUrl}
      />
    </ContentWrapper>
  );

  const addToFavorite = (id: number) => {
    const data = {
      articleId: id,
      favoriteFlag: isFavorite ? 0 : 1,
    };
    dispatch(favouriteArticleActions.addToFavorite(data));
  };

  const handleClick = () => {
    toggleVisible();
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('alertPanel_sales_tigger_clicked', 0, {
        ...articleAlert && { sales_trigger: articleAlert.toLowerCase() },
        host,
      }),
    ));
  };

  return (
    <>
      <ArticleWrapper tiggerColor={trigger} className={`article-item alert-article improve-article ${isRead ? '' : 'active'}`}>
        <div
          className="profile-area"
          onClick={showDrawer ? handleClick : () => { }}
          onKeyPress={showDrawer ? toggleVisible : () => { }}
          role="button"
          aria-label="Article is clickable"
          tabIndex={0}
        >
          <Image backgroundImage={setImageSize(image, 72, 72, articleId)} />
        </div>
        <div className="text-area">
          <h6
            className="heading heading-width text-truncate-three-line"
            onClick={showDrawer ? handleClick : () => { }}
            onKeyPress={showDrawer ? toggleVisible : () => { }}
            aria-hidden="true"
          >
            {heading}
          </h6>
          {articleAlert && (
            <ArticleTag tagColor={trigger} className="tagArticle c-auto">
              {articleAlert}
            </ArticleTag>
          )}
          <div className="bottom-action-wrapper">
            <div
              className="bottom-tips"
              onClick={showDrawer ? handleClick : () => { }}
              onKeyPress={showDrawer ? toggleVisible : () => { }}
              role="button"
              aria-label="Toggle drawer"
              tabIndex={0}
            >
              <div className="tips domain">{domain}</div>
              <div className="tips separator date">{date}</div>
              <div className="tips industry">{industry}</div>
            </div>
            <div className="action-sharing">
              <span
                className="favorite-icon"
                onClick={() => addToFavorite(articleId)}
                onKeyPress={() => addToFavorite(articleId)}
                tabIndex={0}
                aria-label="Add to favourites"
                role="button"
              >
                <FavoriteIcon color={`${isFavorite ? favouriteArticleColor : whiteColor}`} />
              </span>
              <Popover
                placement="left"
                content={content}
                trigger="click"
                getPopupContainer={(e: HTMLElement) => e}
                className="popover-sharing"
              >
                <div aria-hidden="true">
                  <span className="d-none">share-icon</span>
                  <ShareArticleIcon />
                </div>
              </Popover>
            </div>
          </div>
        </div>
      </ArticleWrapper>
      {
        showDrawer
        && (
          <ArticleDrawer
            visible={visible}
            toggleVisible={toggleVisible}
            articleId={articleId}
            title={heading}
            domain={domain}
            dateTimeStamp={date}
            image={image}
            summary={summary}
            url={redirectUrl}
            alertId={alertId}
            articleAlert={alerts}
          />
        )
      }
    </>
  );
};
ArticleItem.defaultProps = defaultProps;
export default ArticleItem;
